// src/services/interactionService.js
import { generateClient } from 'aws-amplify/api';

// Lazy client initialization to ensure Amplify is configured first
let client = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

// GraphQL operations
const TOGGLE_REVIEW_LIKE = `
  mutation ToggleReviewLike($reviewID: ID!, $reviewType: String!) {
    toggleReviewLike(reviewID: $reviewID, reviewType: $reviewType) {
      id
      reviewID
      userID
      reviewType
      createdAt
    }
  }
`;

const TOGGLE_BOOKMARK = `
  mutation ToggleBookmark($postID: ID!) {
    toggleBookmark(postID: $postID) {
      id
      postID
      userID
      contentType
      notes
      tags
      createdAt
      updatedAt
    }
  }
`;

const TOGGLE_REVIEW_BOOKMARK = `
  mutation ToggleReviewBookmark($reviewID: ID!, $reviewType: String!) {
    toggleReviewBookmark(reviewID: $reviewID, reviewType: $reviewType) {
      id
      reviewID
      userID
      contentType
      reviewType
      notes
      tags
      createdAt
      updatedAt
    }
  }
`;

const SHARE_POST = `
  mutation SharePost($postID: ID!, $message: String) {
    sharePost(postID: $postID, message: $message) {
      id
      postID
      userID
      message
      createdAt
    }
  }
`;

const LIST_REVIEW_LIKES = `
  query ListReviewLikes($reviewID: ID!, $userID: ID) {
    listReviewLikes(
      filter: { 
        and: [
          { reviewID: { eq: $reviewID } }
          { userID: { eq: $userID } }
        ]
      }
      limit: 1
    ) {
      items {
        id
        reviewID
        userID
        reviewType
      }
    }
  }
`;

const LIST_BOOKMARKS = `
  query ListBookmarks($postID: ID, $reviewID: ID, $userID: ID!) {
    listBookmarks(
      filter: { 
        userID: { eq: $userID }
      }
      limit: 1
    ) {
      items {
        id
        postID
        reviewID
        userID
        contentType
        reviewType
        notes
        tags
      }
    }
  }
`;

const LIST_POST_BOOKMARKS = `
  query ListPostBookmarks($postID: ID!, $userID: ID!) {
    listBookmarks(
      filter: { 
        and: [
          { userID: { eq: $userID } }
          { postID: { eq: $postID } }
        ]
      }
      limit: 1
    ) {
      items {
        id
        postID
        userID
        contentType
        notes
        tags
      }
    }
  }
`;

const LIST_REVIEW_BOOKMARKS = `
  query ListReviewBookmarks($reviewID: ID!, $userID: ID!) {
    listBookmarks(
      filter: { 
        and: [
          { userID: { eq: $userID } }
          { reviewID: { eq: $reviewID } }
        ]
      }
      limit: 1
    ) {
      items {
        id
        reviewID
        userID
        contentType
        reviewType
        notes
        tags
      }
    }
  }
`;

export const interactionService = {
  // Like functionality for reviews
  async toggleReviewLike(reviewID, reviewType) {
    try {
      const response = await getClient().graphql({
        query: TOGGLE_REVIEW_LIKE,
        variables: { reviewID, reviewType }
      });
      return response.data.toggleReviewLike;
    } catch (error) {
      console.error('Error toggling review like:', error);
      throw error;
    }
  },

  // Check if review is liked by user
  async isReviewLiked(reviewID, userID) {
    try {
      const response = await getClient().graphql({
        query: LIST_REVIEW_LIKES,
        variables: { reviewID, userID }
      });
      return response.data.listReviewLikes.items.length > 0;
    } catch (error) {
      console.error('Error checking review like status:', error);
      return false;
    }
  },

  // Bookmark functionality for posts
  async toggleBookmark(postID) {
    try {
      const response = await getClient().graphql({
        query: TOGGLE_BOOKMARK,
        variables: { postID }
      });
      return response.data.toggleBookmark;
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      throw error;
    }
  },

  // Bookmark functionality for reviews
  async toggleReviewBookmark(reviewID, reviewType) {
    try {
      const response = await getClient().graphql({
        query: TOGGLE_REVIEW_BOOKMARK,
        variables: { reviewID, reviewType }
      });
      return response.data.toggleReviewBookmark;
    } catch (error) {
      console.error('Error toggling review bookmark:', error);
      throw error;
    }
  },

  // Check if post is bookmarked by user
  async isBookmarked(postID, userID) {
    try {
      const response = await getClient().graphql({
        query: LIST_POST_BOOKMARKS,
        variables: { postID, userID }
      });
      return response.data.listBookmarks.items.length > 0;
    } catch (error) {
      console.error('Error checking bookmark status:', error);
      return false;
    }
  },

  // Check if review is bookmarked by user
  async isReviewBookmarked(reviewID, userID) {
    try {
      const response = await getClient().graphql({
        query: LIST_REVIEW_BOOKMARKS,
        variables: { reviewID, userID }
      });
      return response.data.listBookmarks.items.length > 0;
    } catch (error) {
      console.error('Error checking review bookmark status:', error);
      return false;
    }
  },

  // Share functionality for posts
  async sharePost(postID, message = '') {
    try {
      const response = await getClient().graphql({
        query: SHARE_POST,
        variables: { postID, message }
      });
      return response.data.sharePost;
    } catch (error) {
      console.error('Error sharing post:', error);
      throw error;
    }
  },

  // Universal share function (handles both posts and reviews)
  async shareContent(contentType, contentId, message = '') {
    if (contentType === 'post') {
      return this.sharePost(contentId, message);
    } else if (contentType === 'review') {
      // For reviews, we can create a social post about the review
      const shareData = {
        title: `Check out this ${contentType} review`,
        text: message || `Great review worth sharing!`,
        url: window.location.href
      };
      
      if (navigator.share) {
        return navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        return { shared: true, method: 'clipboard' };
      }
    }
  },

  // Universal like function
  async toggleLike(contentType, contentId, reviewType = null) {
    if (contentType === 'post') {
      // Use existing post like functionality
      const response = await getClient().graphql({
        query: `
          mutation ToggleLike($postID: ID!) {
            toggleLike(postID: $postID) {
              id
              likesCount
            }
          }
        `,
        variables: { postID: contentId }
      });
      return response.data.toggleLike;
    } else if (contentType === 'review') {
      return this.toggleReviewLike(contentId, reviewType);
    }
  },

  // Universal bookmark function for both posts and reviews
  async toggleBookmarkUniversal(contentType, contentId, reviewType = null) {
    if (contentType === 'post') {
      return this.toggleBookmark(contentId);
    } else if (contentType === 'review') {
      return this.toggleReviewBookmark(contentId, reviewType);
    }
  },

  // Check universal like status
  async isLiked(contentType, contentId, userID, reviewType = null) {
    if (contentType === 'post') {
      // Check post likes
      const response = await getClient().graphql({
        query: `
          query ListLikes($postID: ID!, $userID: ID!) {
            listLikes(
              filter: { 
                and: [
                  { postID: { eq: $postID } }
                  { userID: { eq: $userID } }
                ]
              }
              limit: 1
            ) {
              items {
                id
              }
            }
          }
        `,
        variables: { postID: contentId, userID }
      });
      return response.data.listLikes.items.length > 0;
    } else if (contentType === 'review') {
      return this.isReviewLiked(contentId, userID);
    }
    return false;
  },

  // Check universal bookmark status
  async isBookmarkedUniversal(contentType, contentId, userID) {
    if (contentType === 'post') {
      return this.isBookmarked(contentId, userID);
    } else if (contentType === 'review') {
      return this.isReviewBookmarked(contentId, userID);
    }
    return false;
  }
};